import React from "react";
import { graphql, useFragment } from "react-relay";

import { faPlus, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { isNil } from "lodash";

import { getFirstUserFacingError, inCompanionMode } from "~/lib/utils";
import { inVideocallMode } from "~/lib/utils";
import { useAnalyticsTrack } from "~/meetingRecorder/utils";
import { toast, Tooltip } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";

import {
	useAskFellowContextFromURL,
	useClearConversationContextMutation,
	useSetConversationContextMutation,
} from "./AskFellowHooks";

import type { AskFellowContext_conversation$key } from "~/__generated__/AskFellowContext_conversation.graphql";

import styles from "./AskFellowContext.module.css";

function ContextPill({
	conversationKey,
	className,
	contextFooterRef,
}: {
	conversationKey: AskFellowContext_conversation$key;
	className?: string;
	contextFooterRef?: React.RefObject<HTMLDivElement>;
}) {
	const conversation = useFragment(
		graphql`
			fragment AskFellowContext_conversation on AskCopilotConversation {
				id
				context {
					name
					filters
				}
			}
		`,
		conversationKey,
	);

	const conversationId = conversation.id;
	const [clearConversationContext, mutationInProgress] = useClearConversationContextMutation();
	const onClearConversationContext = React.useCallback(async () => {
		if (mutationInProgress) return;

		try {
			await clearConversationContext(conversationId);
		} catch (e) {
			const message = getFirstUserFacingError(e) ?? "Something went wrong; please try again later.";
			toast.error(message);
		}
	}, [conversationId, clearConversationContext, mutationInProgress]);

	const conversationContext = conversation.context;
	const isContextSet = !!conversationContext;

	if (!isContextSet) {
		return null;
	}

	const contextName = conversationContext.name;
	const parsedFilters = JSON.parse(conversationContext.filters ?? "{}");
	const areFiltersPresent = Object.values(parsedFilters).some(value => !isNil(value));

	const getFilterDetails = () => {
		if (!areFiltersPresent) return "";

		return Object.entries(parsedFilters)
			.filter(([_, value]) => !isNil(value))
			.map(([key, value]) => {
				// Format the filter keys to be more readable
				const formattedKey = key
					.replace(/_/g, " ")
					.split(" ")
					.map(word => word.charAt(0).toUpperCase() + word.slice(1))
					.join(" ");

				// Format the value depending on its type
				let formattedValue = value;
				if (Array.isArray(value)) {
					formattedValue = value.join(", ");
				} else if (typeof value === "string" && /[a-z][A-Z]/.test(value)) {
					// Convert camelCase to separate words
					const words = value.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
					// Capitalize only the first letter of the sentence
					formattedValue = words.charAt(0).toUpperCase() + words.slice(1);
				}

				return `${formattedKey}: ${formattedValue}`;
			})
			.join("\n");
	};

	const inVideoOrCompanionMode = inVideocallMode || inCompanionMode;

	return (
		<div ref={contextFooterRef} className={cx(styles.contextPill, className)}>
			<p>About</p>
			<span className={styles.textContainer}>
				<b className={styles.contextName}>{contextName}</b>
				{areFiltersPresent && (
					<Tooltip content={<pre className={styles.filterTooltip}>{getFilterDetails()}</pre>} placement="bottom">
						<span className={styles.filtersText}>&nbsp;& Filters</span>
					</Tooltip>
				)}
			</span>
			{!inVideoOrCompanionMode && <IconButton title="Clear" icon={faTimes} onClick={onClearConversationContext} />}
		</div>
	);
}

export function ContextSuggestion({ conversationId }: { conversationId: string }) {
	const context = useAskFellowContextFromURL();
	const dispatchAnalyticsTrack = useAnalyticsTrack();

	const [setConversationContext, mutationInProgress] = useSetConversationContextMutation();
	const onSetConversationContext = React.useCallback(async () => {
		if (mutationInProgress) return;

		try {
			await setConversationContext(conversationId, context?.id, context?.filters);
			dispatchAnalyticsTrack("[Ask Copilot] Clicked on suggested context", {
				contextId: context?.id,
				contextType: context?.type,
			});
		} catch (e) {
			const message = getFirstUserFacingError(e) ?? "Something went wrong; please try again later.";
			toast.error(message);
		}
	}, [
		conversationId,
		context?.id,
		context?.filters,
		context?.type,
		setConversationContext,
		dispatchAnalyticsTrack,
		mutationInProgress,
	]);

	if (!context) {
		return null;
	}

	const areFiltersPresent = Object.values(context.filters ?? {}).some(filter => filter !== undefined);

	return (
		<Tooltip content="Add context" delay={500}>
			<button className={styles.contextSuggestion} onClick={onSetConversationContext}>
				<FontAwesomeIcon icon={faPlus} />
				<span className={styles.contextName}>{context.name}</span>
				{areFiltersPresent && <span className={styles.filtersText}> & Filters</span>}
			</button>
		</Tooltip>
	);
}

export default ContextPill;
